import { addTranslations } from "@app/i18n";

export const RESOURCE_NAME = "ObjectiveDefinition";

export const keys = addTranslations(RESOURCE_NAME, {
    target_desc: {
        en: "Link to an indicator to track progress toward the objective’s targets or select “No indicator”",
        fr: "Lien vers un indicateur pour suivre la progression vers les cibles de l'objectif ou sélectionnez “Aucun indicateur”",
    },
    status: {
        en: "Status",
        fr: "Statut",
    },
    status_UNKNOWN: {
        en: "Unknown",
        fr: "Inconnu",
    },
    status_DRAFT: {
        en: "Draft",
        fr: "Brouillon",
    },
    status_DEFINED: {
        en: "Validated",
        fr: "Validé",
    },
    status_COMMITTED: {
        en: "Committed",
        fr: "Engagé",
    },
    status_ARCHIVED: {
        en: "Archived",
        fr: "Archivé",
    },
    name_placeholder: {
        en: "Click to set name",
        fr: "Cliquez pour définir le nom",
    },
    disabled_targets_tooltip: {
        en: "Cannot be edited if targets are set",
        fr: "Ne peut pas être modifié si des objectifs chiffrés sont définis",
    },
    unassigned: {
        en: "Unassigned",
        fr: "Non assigné",
    },
    success_update: {
        en: "Objective updated successfully",
        fr: "Objectif mis à jour avec succès",
    },
    error_update: {
        en: "Error updating objective",
        fr: "Erreur lors de la mise à jour de l'objectif",
    },
    content_subtitle: {
        en: "Define objective",
        fr: "Définissez votre objectif",
    },
    show_example: {
        en: "Show an example",
        fr: "Afficher un exemple",
    },
    name_desc: {
        en: "Summarize your objective",
        fr: "Résumez votre objectif",
    },
    theme_desc: {
        en: "Select a theme or subtheme ",
        fr: "Sélectionnez un thème ou un sous-thème",
    },
    delete: {
        en: "Delete objective",
        fr: "Supprimer l'objectif",
    },
    delete_modal_title: {
        en: "Delete objective",
        fr: "Supprimer l'objectif",
    },
    delete_modal_description: {
        en: "You are about to delete this objective. This action can not be undone.",
        fr: "Vous êtes sur le point de supprimer cet objectif. Cette action ne peut pas être annulée.",
    },
    delete_cta: {
        en: "Delete",
        fr: "Supprimer",
    },
    delete_cancel: {
        en: "Cancel",
        fr: "Annuler",
    },
    success_delete: {
        en: "Objective deleted successfully",
        fr: "Objectif supprimé avec succès",
    },
    error_delete: {
        en: "Error deleting objective",
        fr: "Erreur lors de la suppression de l'objectif",
    },
    name_label: {
        en: "Summarize the objective",
        fr: "Résumez l'objectif",
    },
    theme_label: {
        en: "Set the objective’s business entity and theme/subtheme. Selections filter the available indicators.",
        fr: "Définissez l'entité commerciale et le thème/sous-thème de l'objectif. Les sélections filtrent les indicateurs disponibles.",
    },
    combobox_no_indicator: {
        en: "No indicator",
        fr: "Aucun indicateur",
    },
    combobox_placeholder: {
        en: "Indicator",
        fr: "Indicateur",
    },
    combobox_browse_all_indicators: {
        en: "Browse all indicators",
        fr: "Voir tous les indicateurs",
    },
    historical_data_title: {
        en: "Historical data from linked indicator:",
        fr: "Données de l'indicateur lié :",
    },
    target_due_date: {
        en: "Due date",
        fr: "Échéance",
    },
    target_value: {
        en: "Target value",
        fr: "Valeur",
    },
    no_indicator_selected_button: {
        en: "Select Indicator",
        fr: "Sélectionner un indicateur",
    },
    update_success: {
        en: "Objective updated",
        fr: "Objectif mis à jour",
    },
    update_error: {
        en: "Error updating objective",
        fr: "Erreur lors de la mise à jour de l'objectif",
    },
    add_target: {
        en: "Add target",
        fr: "Ajouter une cible",
    },

    hint_name: {
        en: `<ul>An objective’s name is often similar to the name of it’s linked indicator, but starting with an action word like “increase,” “decrease,” or “maintain.”<br key="br-1"/><br key="br-2"/>
<li key="example-1"> Example 1: Increase the share of renewable sources in total energy consumption to reach 50% by 2030</li>
<li key="example-2"> Example 2: Reduce total energy consumption in operations</li>
<li key="example-3"> Example 3: Train 100% of employees on energy saving gestures by end of 2025</li></ul>`,
        fr: `<ul>Le nom d’un objectif ressemble souvent à celui de l’indicateur associé, mais commence généralement par un verbe d’action comme “augmenter”, “réduire” ou “maintenir”. <br key="br-1"/><br key="br-2"/>
<li key="example-1"> Exemple 1 : Augmenter la part des énergies renouvelables dans la consommation totale d’énergie pour atteindre 50% d’ici 2030</li>  
<li key="example-2"> Exemple 2 : Réduire la consommation totale d’énergie dans les opérations</li>  
<li key="example-3"> Exemple 3 : Former 100% des employés aux écogestes d’ici fin 2025</li></ul>`,
    },
    hint_indicator: {
        en: "Indicators are concrete company metrics. Linking to an indicator makes the objective more specific and measurable. Create and edit indicators on the Reporting page.",
        fr: "Les indicateurs sont des indicateurs concrets de l’entreprise. Lier un indicateur rend l’objectif plus spécifique et mesurable. Créez et modifiez des indicateurs sur la page Reporting.",
    },
    hint_theme_subtheme: {
        en: `Objectives outline company targets within specific business areas called themes and subthemes. Frameworks encourage at least 3 objectives per each required subtheme. Not all themes require objectives (such as “General Information” or “Governance”), but you can still create objectives in these areas to support your strategy.
        <br/>
        Frameworks determine which subthemes are required, but often only a subset of those subthemes are relevant to each company. Set which subthemes apply to your company on the Material Topics page.`,
        fr: `Les objectifs définissent les cibles de l’entreprise dans des domaines spécifiques appelés thèmes et sous-thèmes. Les référentiels recommandent de définir au moins 3 objectifs par sous-thème requis. Tous les thèmes ne nécessitent pas d’objectifs (comme "General Information" ou "Governance"), mais vous pouvez tout de même en créer dans ces domaines pour soutenir votre stratégie.
        <br/>
Ce sont les référentiels qui déterminent les sous-thèmes obligatoires, mais souvent, seuls certains sont pertinents pour chaque entreprise. Vous pouvez définir les sous-thèmes applicables à votre entreprise depuis la page Material Topics.
`,
    },
    guidance_trigger: {
        en: "Show guidance",
        fr: "Afficher les conseils",
    },
    guidance_trigger_open: {
        en: "Show guidance",
        fr: "Afficher les conseils",
    },
    guidance_trigger_close: {
        en: "Hide guidance",
        fr: "Masquer les conseils",
    },
    guidance_title: {
        en: "Ditto Guidance",
        fr: "Conseils Ditto",
    },
    guidance_description: {
        en: `<p>We recommend setting both a</p>
<ul>
  <li key="long-term-target-1"><strong>long-term target</strong> that is ambitious.</li>
  <li key="short-term-target-2"><strong>short-term target</strong> that demonstrates how the company plans to progress towards the final goal.</li>
</ul>
<br key="br-1" />
<p>Objectives must be SMART to be policy-compliant:</p><br key="br-2">
<ul>
  <li key="specific"><strong>Specific:</strong> Clearly defined and unambiguous.</li>
  <li key="measurable"><strong>Measurable:</strong> Quantifiable, with clear criteria for success.</li>
  <li key="achievable"><strong>Achievable:</strong> Realistic and reachable within available resources and constraints.</li>
  <li key="relevant"><strong>Relevant:</strong> Aligned with broader business goals and priorities.</li>
  <li key="time-bound"><strong>Time-bound:</strong> Set within a specific timeframe for completion.</li>
</ul>`,
        fr: `<p>Nous recommandons de définir à la fois :</p>
<ul>
  <li key="long-term-target-1"><strong>un objectif long terme</strong> ambitieux.</li>
  <li key="short-term-target-2"><strong>un objectif court terme</strong> qui montre comment l’entreprise prévoit de progresser vers l’objectif final.</li>
</ul>
<br key="br-1" />
<p>Les objectifs doivent être SMART pour être conformes aux exigences :</p><br key="br-2">
<ul>
  <li key="specific"><strong>Spécifique :</strong> Définis clairement et sans ambiguïté.</li>
  <li key="measurable"><strong>Mesurable :</strong> Quantifiables, avec des critères de réussite précis.</li>
  <li key="achievable"><strong>Atteignable :</strong> Réalistes et réalisables en tenant compte des ressources et contraintes disponibles.</li>
  <li key="relevant"><strong>Pertinent :</strong> Alignés avec les priorités et les objectifs globaux de l’entreprise.</li>
  <li key="time-bound"><strong>Temporellement défini :</strong> Fixés dans un délai précis.</li>
</ul>
`,
    },
    change_theme_subtheme_modal_title: {
        en: "Change theme/subtheme",
        fr: "Changer le thème/sous-thème",
    },
    change_theme_subtheme_modal_description: {
        en: "This action will reset the indicator selection, as well as the set targets. Do you want to continue?",
        fr: "Cette action réinitialisera la sélection de l'indicateur et les cibles définies. Voulez-vous continuer ?",
    },
    change_theme_subtheme_modal_cta: {
        en: "Change",
        fr: "Changer",
    },
    change_theme_subtheme_modal_cancel: {
        en: "Cancel",
        fr: "Annuler",
    },
});
